/**
 * Packages.
 * 
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/
 */
import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls, BlockControls } from '@wordpress/block-editor';
import {
	Panel, PanelBody, PanelRow, TabPanel, BaseControl, Button,
	TextControl, SelectControl, FormTokenField, ToggleControl,
	__experimentalNumberControl as NumberControl, RadioControl
} from '@wordpress/components';
import { useState } from '@wordpress/element';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './editor.scss';

/**
 * Declaration of block functions and components.
 */

/**
 * Returns a translated option object for use in a SelectControl component.
 * 
 * @param {*} label Option label.
 * @param {*} value Option value.
 * @param {*} disabled Is it disabled. Default: false.
 * @returns {Object} Option object for use in SelectControl.
 */
function _o( label, value, disabled = false ) {
	let option = {
		label: __( label, 'showcase-creator' ),
		value: value
	};
	if ( disabled === true ) {
		option.disabled = true;
	}
	return option;
}

/**
 * Loads data from REST API.
 * 
 * @param { string } setStatus 
 * @returns { Array|Promise }
 */
async function loadData( setStatus ) {
	const data = window.showcaseCreator.data;
	data.status = 'pending';
	let r = await window.showcaseCreator.rest();
	if ( r ) {
		data.status = 'ready';
	} else {
		loadData( setStatus );
		return;
	}
	
	// prepare objects
	let taxs = {}, terms = {};
	r.types.map( t => t.name ).forEach( t => taxs[t] = [] );
	r.taxs.map( f => f.name ).forEach( t => terms[t] = [] );
	
	// process layouts
	data.layouts = r.layouts.map( d => {
		return { label: d.title, value: d.ID }
	} );
	
	// process types
	data.types = r.types;
	
	// process taxonomies
	r.taxs.forEach( tax => { tax.object_type.forEach( type => {
		if ( ! ( type in taxs ) ) { taxs[type] = []; }
		taxs[type].push( { label: tax.label, value: tax.name } ); } ); } );
	data.taxs = taxs;
	
	// process terms
	r.terms.forEach( t => {
		if ( ! ( t.taxonomy in terms ) ) {
			terms[t.taxonomy] = [];
		}
		terms[t.taxonomy].push( {
			label: t.name + (
				r.terms.filter( f => f.name === o.name).length > 1 ?
					' (ID: '+o.id+')' :
					''
			),
			value: t.term_id
		} );
	} );
	data.terms = terms;
	
	// process authors
	data.authors = r.authors.map( o => { return {
		label: o.name + (
			r.authors.filter( f => f.name === o.name ).length > 1 ?
				' (ID: '+o.id+')' :
				''
		),
		value: o.id }
	} );
	
	// process ordered lists
	data.ordered_lists = r.ordered_lists.map( k => {
		return { label: k.label, value: k.slug }
	} );
	setStatus( data.status );
	return Promise.resolve( data );
}

/**
 * Fetches posts for the current post type.
 * 
 * @param {String} type - current post type.
 * @param {*} setPosts - pass the useState function.
 * @returns {Array|Promise}
 */
async function loadPosts( type, setPosts ) {
	let r = await window.showcaseCreator.rest( {
		get: 'posts', args: { post_type: type }
	} );
	if ( ! r || ! ( 'posts' in r ) ) {
		setTimeout( () => loadPosts( type, setPosts ), 1000 );
		return [];
	}
	r = await r.posts.map( o => {
		if ( r.posts.filter( f => f.post_title === o.post_title ).length > 1 ) {
			return { label: o.post_title + ' (ID: '+ o.ID +')', value: o.ID }
		} else {
			return { label: o.post_title, value: o.ID }
		}
	} );
	setPosts( r );
	return Promise.resolve( r );
}

// Mime type suggestions
const mimeSuggestions = [
	'image',
	'video',
	'audio',
	'image/jpeg',
	'image/pjpeg',
	'image/jpeg',
	'image/pjpeg',
	'image/png',
	'image/gif',
	'image/x-icon',
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/mspowerpoint',
	'application/powerpoint',
	'application/vnd.ms-powerpoint',
	'application/x-mspowerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'application/mspowerpoint',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
	'application/vnd.oasis.opendocument.text',
	'application/excel',
	'application/vnd.ms-excel',
	'application/x-excel',
	'application/x-msexcel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/octet-stream',
	'audio/mpeg3',
	'audio/x-mpeg-3',
	'video/mpeg',
	'video/x-mpeg',
	'audio/m4a',
	'audio/ogg,',
	'audio/wav',
	'audio/x-wav',
	'video/mp4',
	'video/x-m4v',
	'video/quicktime',
	'video/x-ms-asf',
	'video/x-ms-wmv',
	'application/x-troff-msvideo',
	'video/avi',
	'video/msvideo',
	'video/x-msvideo',
	'audio/mpeg',
	'video/mpeg',
	'video/ogg',
	'video/3gpp',
	'audio/3gpp',
	'video/3gpp2',
	'audio/3gpp2',
];

// Months
const monthOptions = [
	_o( '', '' ),
	_o( 'Jan', '1' ),
	_o( 'Feb', '2' ),
	_o( 'Mar', '3' ),
	_o( 'Apr', '4' ),
	_o( 'May', '5' ),
	_o( 'Jun', '6' ),
	_o( 'Jul', '7' ),
	_o( 'Aug', '8' ),
	_o( 'Sep', '9' ),
	_o( 'Oct', '10' ),
	_o( 'Nov', '11' ),
	_o( 'Dec', '12' )
];

// Tab: Query
const Tab_Query = ( { attributes, setAttributes, data, onTypeChange } ) => {
	return <Panel>
		<PanelBody>
			<PanelRow>
				<SelectControl
					label={ __( 'Post type', 'showcase-creator' ) }
					value={ attributes.postType }
					options={ [
						_o( 'Select a post type', '', true ),
						...data.types.map( ( t ) => {
							return { label: t.label, value: t.name }
						} )
					] }
					onChange={ ( v ) => {
						setAttributes( {
							postType: v,
							taxQueries: [ { taxonomy: '', terms: [], operator: 'IN' } ]
						} );
						onTypeChange( v );
					} }
				/>
			</PanelRow>
			{ attributes.postType === 'attachment' && (
			<PanelRow>
				<FormTokenField
					label={ __( 'Mime types', 'showcase-creator' ) }
					value={ attributes.mimeTypes }
					onChange={ ( v ) => {
						setAttributes( { mimeTypes: v } );
					} }
					suggestions={ mimeSuggestions }
					tokenizeOnSpace
				/>
			</PanelRow> ) }
			{ attributes.taxQueries.length > 0 && (
			<ol className={ 'sc-inspector-controls-taxqueries' }>
				{
					attributes.taxQueries.map( ( query, i, all ) =>
						<li key={ i }>
							<PanelRow>
								<SelectControl
									label={ __( 'Taxonomy', 'showcase-creator' ) }
									style={ { margin: '0' } }
									value={ query.taxonomy }
									options={ [
										_o( 'Any taxonomy', '', false ),
										...(
											attributes.postType in data.taxs
											? data.taxs[attributes.postType]
											: []
										)
									] }
									onChange={ ( v ) => {
										let temp = [...all];
										temp[i].taxonomy = v;
										temp[i].terms = [];
										setAttributes( { taxQueries: temp } );
									} }
								/>
							</PanelRow>
							{ query.taxonomy && (
							<PanelRow>
								<FormTokenField
									label={ __( 'Terms', 'showcase-creator' ) }
									style={ { margin: '0' } }
									value={
										query.terms.map( ( v ) => {
											return (
												query.taxonomy in data.terms
												? data.terms[query.taxonomy]
												: [ { label: v, value: v } ]
											).find( f => f.value === v ).label;
										} )
									}
									suggestions={ (
										query.taxonomy in data.terms
										? data.terms[query.taxonomy]
										: [ { label: v, value: v } ]
										).map( t => t.label )
									}
									__experimentalExpandOnFocus
									onChange={ ( v ) => {
										const terms = data.terms[query.taxonomy];
										if ( ! terms ) { return; }
										let taxTerms = terms.map( t => t.label ),
											filtered = v
												.filter( f => taxTerms.includes( f ) )
												.map(
													( l ) => data.terms[query.taxonomy]
													.find( f => f.label === l ).value ),
											temp = [ ...all ];
										temp[i].terms = filtered;
										setAttributes( { taxQueries: temp } ) } }
									/>
							</PanelRow> ) }
							{ query.taxonomy && (
							<PanelRow>
								<SelectControl
									label={ __( 'Operator', 'showcase-creator' ) }
									style={ { margin: '0' } }
									value={ query.operator }
									options={ [
										_o( 'IN any of the selected term(s)', 'IN' ),
										_o( 'NOT IN the selected term(s)', 'NOT IN' ),
										_o( 'AND - present in all terms', 'AND' )
									] }
									onChange={ ( v ) => {
										let temp = [ ...attributes.taxQueries ];
										temp[i].operator = v;
										setAttributes( { taxQueries: temp } );
									} }
								/>
							</PanelRow> ) }
							<Button
								variant='secondary'
								isDestructive={ true }
								size={ 'small' }
								className={ [
									'sc-wide-button',
									'sc-gap-above'
								] }
								onClick={ () => {
									let temp = [ ...attributes.taxQueries ];
									temp.splice( i,1 );
									setAttributes( { taxQueries: temp } );
								} }
							>{ __( 'Remove tax. query', 'showcase-creator' ) }</Button>
						</li>
					)
				}
			</ol> ) }
			{ attributes.postType !== '' && (
			<PanelRow>
				<Button
					className={ 'sc-wide-button' }
					variant='primary' size={ 'small' }
					onClick={ () =>
						setAttributes( {
							taxQueries: [
								...attributes.taxQueries,
								{ taxonomy: '', terms: [], operator: 'IN' }
							]
						} )
					}
				>{ __( 'Add new tax. query', 'showcase-creator' ) }</Button>
			</PanelRow> ) }
			{ attributes.taxQueries.length > 0 && (
			<PanelRow>
				<RadioControl
					label={ __( 'Relationship between taxonomies', 'showcase-creator' ) }
					selected={ attributes.taxRelation }
					onChange={ ( v ) => setAttributes( { taxRelation: v } ) }
					options={ [
						_o( 'AND = All must be TRUE', 'AND' ),
						_o( 'OR = at least one must be TRUE', 'OR' )
					] }
				/>
			</PanelRow> )}
		</PanelBody>
	</Panel>
}

// Tab: Order
const Tab_Order = ( { attributes, setAttributes, data } ) => {
	return <Panel>
		<PanelBody>
			<PanelRow>
				<SelectControl
					label={ __( 'Order', 'showcase-creator' ) }
					value={ attributes.order }
					options={ [
						_o( 'Ascending', 'ASC' ),
						_o( 'Descending', 'DESC' )
					] }
					onChange={ ( v ) => setAttributes( { order: v } ) }
				/>
			</PanelRow>
			<PanelRow>
				<SelectControl
					label={ __( 'Order by', 'showcase-creator' ) }
					value={ attributes.orderBy }
					options={ [
						_o( 'Date', 'date' ),
						_o( 'Modified', 'modified' ),
						_o( 'Title', 'title' ),
						_o( 'Author', 'author' ),
						_o( 'Menu order', 'menu_order' ),
						_o( 'Random', 'rand' ),
						_o( 'Meta value (alphabetical)', 'meta_value' ),
						_o( 'Meta value (numeric)', 'meta_value_num' ),
						_o( 'Relevance (search)', 'relevance' ),
						_o( 'Post ID', 'ID' ),
						_o( 'Parent ID', 'parent' ),
						_o( 'Comment count', 'comment_count' ),
						_o( 'Ordered List', 'ordered_list' )
					] }
					onChange={ ( v ) => setAttributes( { orderBy: v } ) }
				/>
			</PanelRow>
			{ ( attributes.orderBy === 'ordered_list' ) && (
			<PanelRow>
				<SelectControl
					label={ __( 'Ordered List', 'showcase-creator' ) }
					value={ attributes.orderList }
					options={ [
						data.ordered_lists.length
							? _o( 'Select an Ordered List', '', true )
							: _o( 'No Ordered Lists', '', true ),
						...data.ordered_lists
					] }
					onChange={ ( v ) => setAttributes( { orderList: v } ) }
				/>
			</PanelRow> ) }
			{ [ 'meta_value_num', 'meta_value' ].includes( attributes.orderBy ) && (
			<PanelRow>
				<TextControl
					label={ __( 'Meta key', 'showcase-creator' ) }
					value={ attributes.metaKey }
					onChange={ ( v ) => setAttributes( { metaKey: v } ) }
				/>
			</PanelRow> ) }
		</PanelBody>
	</Panel>
}

// Tab: Authors
const Tab_Authors = ( { attributes, setAttributes, data } ) => {
	return <Panel>
		<PanelBody>
			<PanelRow>
				<SelectControl
					label={ __( 'Author', 'showcase-creator' ) }
					value={ attributes.author }
					options={ [
						_o( 'Any author', '' ),
						...data.authors
					] }
					onChange={ ( v ) => setAttributes( { author: v } ) }
				/>
			</PanelRow>
			<PanelRow>
				<RadioControl
					label={ __( 'Include / exclude', 'showcase-creator' ) }
					selected={ attributes.authorIn }
					onChange={ ( v ) => setAttributes( { authorIn: v } ) }
					options={ [
						_o( 'Include authors', '1' ),
						_o( 'Exclude authors', '0' ),
					] }
				/>
			</PanelRow>
			<PanelRow>
				<FormTokenField
					label={
						parseInt( attributes.authorIn )
						? __( 'Include authors', 'showcase-creator' )
						: __( 'Exclude authors', 'showcase-creator' )
					}
					__experimentalExpandOnFocus
					maxSuggestions={ 5 }
					value={
						attributes.authors.map( a => (
							data.authors.find( f => f.value === a )
							|| { label: '' }
						).label ).filter( a => a )
					}
					suggestions={ data.authors.map( a => a.label ) }
					onChange={ ( v ) => setAttributes( {
						authors: v.map( a => (
							data.authors.find( f => f.label === a )
							|| { value: '' }
						).value ).filter( a => a )
					} ) }
				/>
			</PanelRow>
		</PanelBody>
	</Panel>
}

// Tab: Posts
const Tab_Posts = ( { attributes, setAttributes, posts = [], data = {} } ) => {
	return <Panel>
		<PanelBody>
			<PanelRow>
				<BaseControl
					help={ __( '-1 to show all posts', 'showcase-creator' ) }
					__nextHasNoMarginBottom={ true }
				>
					<NumberControl
						label={ __( 'Maximum number of posts', 'showcase-creator' ) }
						min={-1}
						value={ Number( attributes.postsToDisplay ) }
						onChange={ ( v ) => {
							setAttributes( { postsToDisplay: String( v ) } )
						} }
					/>
				</BaseControl>
			</PanelRow>
			{ attributes.postType && (
				<PanelRow>
					<FormTokenField
						label={ __( 'Posts to include', 'showcase-creator' ) }
						__experimentalExpandOnFocus maxSuggestions={ 5 }
						value={
							attributes.includePosts.map( p => (
								posts.find( f => f.value === p )
								|| { label: '' }
							).label ).filter( p => p )
						}
						suggestions={ posts.map( p => p.label ) }
						onChange={ ( v ) =>	setAttributes( {
							includePosts: v.map( p => (
								posts.find( f => f.label === p )
								|| { value: '' }
							).value	).filter( p => p )
						} )	}
					/>
				</PanelRow> ) }
			{ attributes.postType &&
				data.types.find( f => f.name === attributes.postType ).hierarchical && (
					<PanelRow>
						<ToggleControl
							label={ __( 'Top level only?', 'showcase-creator' ) }
							help={ __(
								'Whether to ignore posts that are children of others',
								'showcase-creator'
							) }
							checked={ attributes.topLevelOnly }
							onChange={ ( v ) => setAttributes( { topLevelOnly: v } ) }
						/>
					</PanelRow>
			) }
			{ attributes.postType &&
				data.types.find( f => f.name === attributes.postType ).hierarchical &&
				! attributes.topLevelOnly && (
					<PanelRow>
						<FormTokenField
							label={ __( 'Include posts within', 'showcase-creator' ) }
							__experimentalExpandOnFocus
							maxSuggestions={ 5 }
							value={
								attributes.includeInParents.map( p => (
									posts.find( f => f.value === p )
									|| { label: '' }
								).label ).filter( p => p )
							}
							suggestions={ posts.map( p => p.label ) }
							onChange={ ( v ) =>	setAttributes( {
								includeInParents: v.map( p => (
									posts.find( f => f.label === p )
									|| { value: '' }
								).value	).filter( p => p )
							} )	}
						/>
					</PanelRow>
			) }
			{ ( attributes.includePosts.length ||
				attributes.includeInParents.length ) > 0 && (
					<PanelRow>
						<RadioControl
							label={ __( 'Where to put included posts', 'showcase-creator' ) }
							selected={ attributes.includePostsWhere }
							onChange={ ( v ) => setAttributes( { includePostsWhere: v } ) }
							options={ [
								_o( 'Above', 'above' ),
								_o( 'Below', 'below' ),
								_o( 'Amongst', 'among' ),
								_o( 'Alone', 'alone' )
							] }
						/>
					</PanelRow>
			) }
			{ attributes.postType && (
				<PanelRow>
					<FormTokenField
						label={ __( 'Posts to exclude', 'showcase-creator' ) }
						__experimentalExpandOnFocus
						maxSuggestions={ 5 }
						value={
							attributes.excludePosts.map( p => (
								posts.find( f => f.value === p )
								|| { label: '' }
							).label ).filter( p => p )
						}
						suggestions={ posts.map( p => p.label ) }
						onChange={ ( v ) =>	setAttributes( {
							excludePosts: v.map( p => (
								posts.find( f => f.label === p )
								|| { value: '' }
							).value	).filter( p => p )
						} )	}
					/>
				</PanelRow> ) }
			{ attributes.postType &&
				data.types.find( f => f.name === attributes.postType ).hierarchical &&
				! attributes.topLevelOnly && (
					<PanelRow>
						<FormTokenField
							label={ __( 'Exclude posts within', 'showcase-creator' ) }
							__experimentalExpandOnFocus
							maxSuggestions={ 5 }
							value={
								attributes.excludeInParents.map( p => (
									posts.find( f => f.value === p )
									|| { label: '' }
								).label ).filter( p => p )
							}
							suggestions={ posts.map( p => p.label ) }
							onChange={ ( v ) =>	setAttributes( {
								excludeInParents: v.map( p => (
									posts.find( f => f.label === p )
									|| { value: '' }
								).value	).filter( p => p )
							} )	}
						/>
					</PanelRow>
			) }
			<PanelRow>
				<ToggleControl
					label={ __( 'Ignore sticky posts?', 'showcase-creator' ) }
					checked={ attributes.ignoreSticky }
					onChange={ ( v ) => setAttributes( { ignoreSticky: v } ) }
				/>
			</PanelRow>
		</PanelBody>
	</Panel>
}

// Tab: Date
const Tab_Date = ( { attributes, setAttributes } ) => {
	return <Panel>
		<PanelBody>
			<PanelRow>
				<SelectControl
					label={ __( 'Posts from', 'showcase-creator' ) }
					value={ attributes.dateFilter }
					options={ [
						_o( 'Any date', '' ),
						_o( 'This year', 'year' ),
						_o( 'This month', 'month' ),
						_o( 'This week', 'week' ),
						_o( 'Today', 'today' ),
						_o( 'Year/Month/Day', 'ymd' ),
						_o( 'Some time ago', 'ago' ),
						_o( 'After/Before', 'ab' )
					] }
					onChange={ ( v ) => setAttributes( { dateFilter: v } ) }
				/>
			</PanelRow>
			{ [ 'ymd', 'ab' ].includes( attributes.dateFilter ) && (
			<BaseControl
				label={
					{
						ymd: __( 'a given year, month and/or day', 'showcase-creator' ),
						ab: __( 'After this date', 'showcase-creator' )
					}[attributes.dateFilter]
				}
				help={ __( 'Fill in at least one field', 'showcase-creator' ) }
			>
				<PanelRow className={ 'sc-inspector-controls-row-gap' } >
					<TextControl label={ __( 'Year', 'showcase-creator' ) }
						value={ 'y' in attributes.date ? attributes.date.y : '' }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.y = v;
							setAttributes( { date: temp } );
						} }
					/>
					<SelectControl
						label={ __( 'Month', 'showcase-creator' ) }
						value={ 'm' in attributes.date ? attributes.date.m : '' }
						options={ monthOptions }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.m = v;
							setAttributes( { date: temp } );
						} }
					/>
					<NumberControl
						label={ __( 'Day', 'showcase-creator' ) }
						value={ 'd' in attributes.date ? attributes.date.d : '' }
						min={ 1 }
						max={ 31 }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.d = v;
							setAttributes( { date: temp } );
						} } />
				</PanelRow>
			</BaseControl> ) }
			{ attributes.dateFilter === 'ab' && (
			<BaseControl label={ __( 'Before this date', 'showcase-creator' ) } >
				<PanelRow className={ 'sc-inspector-controls-row-gap' } >
					<TextControl
						label={ __( 'Year', 'showcase-creator' ) }
						value={ 'y2' in attributes.date ? attributes.date.y2 : '' }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.y2 = v;
							setAttributes( { date: temp } );
						} }
					/>
					<SelectControl
						label={ __( 'Month', 'showcase-creator' ) }
						value={ attributes.date[4] }
						options={ monthOptions }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.m2 = v;
							setAttributes( { date: temp } );
						} }
					/>
					<NumberControl
						label={ __( 'Day', 'showcase-creator' ) }
						value={ 'd2' in attributes.date ? attributes.date.d2 : '' }
						min={ 1 }
						max={ 31 }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.d2 = v;
							setAttributes( { date: temp } );
						} }
					/>
				</PanelRow>
			</BaseControl> ) }
			{ attributes.dateFilter === 'ago' && (
			<BaseControl label={ __( 'Some time ago', 'showcase-creator' ) } >
				<PanelRow className={ 'sc-inspector-controls-row-gap' } >
					<SelectControl
						value={ 'p' in attributes.date ? attributes.date.p : 'after' }
						options={ [
							_o( 'After', 'after' ),
							_o( 'Before', 'before' )
						] }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.p = v;
							setAttributes( { date: temp } );
						} }
					/>
					<NumberControl
						value={ 'some' in attributes.date ? attributes.date.some : '1' }
						min={ 1 }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.some = v;
							setAttributes( { date: temp } );
						} }
					/>
					<SelectControl
						value={ 'time' in attributes.date ? attributes.date.time : 'year' }
						options={ [
							_o( 'Year(s)', 'year' ),
							_o( 'Month(s)', 'month' ),
							_o( 'Week(s)', 'week' ),
							_o( 'Day(s)', 'day' )
						] }
						onChange={ ( v ) => {
							let temp = { ...attributes.date };
							temp.time = v;
							setAttributes( { date: temp } );
						} }
					/>
				</PanelRow>
			</BaseControl> ) }
			{ attributes.dateFilter && (
			<PanelRow>
				<ToggleControl
					label={ __( 'Apply to last modified date?', 'showcase-creator' ) }
					help={ __(
						'By default, the publication date is used.',
						'showcase-creator'
					) }
					checked={ attributes.dateModified }
					onChange={ ( v ) => setAttributes( { dateModified: v } ) }
				/>
			</PanelRow>
			) }
		</PanelBody>
	</Panel>
}

// Tab: Pagination
const Tab_Pagination = ( { attributes, setAttributes } ) => {
	return <Panel>
		<PanelBody>
			<PanelRow>
				<RadioControl
					label={ __( 'Pagination', 'showcase-creator' ) }
					onChange={( v ) => setAttributes( { pagination: v } ) }
					options={ [
						_o( 'No pagination',	'disabled' ),
						_o( 'Above posts',	'above' ),
						_o( 'Below posts',	'below' ),
						_o( 'Both',	'both' )
					] }
					selected={ attributes.pagination }
				/>
			</PanelRow>
			{ attributes.pagination !== 'disabled' && (
			<PanelRow>
				<NumberControl
					label={ __( 'Posts per page', 'showcase-creator' ) }
					min={1}
					value={ Number( attributes.postsPerPage ) }
					onChange={( v ) => setAttributes( { postsPerPage: String( v ) } ) }
				/>
			</PanelRow> ) }
			<PanelRow>
				<TextControl
					label={ __( 'Text when no posts found', 'showcase-creator' ) }
					value={ attributes.noPostsText }
					onChange={ ( v ) => setAttributes( { noPostsText: v } ) }
				/>
			</PanelRow>
		</PanelBody>
	</Panel>
}

// Tab: More
const Tab_More = ( { attributes, setAttributes } ) => {
	return <Panel>
		<PanelBody>
			<PanelRow>
				<ToggleControl
					label={ __( 'With featured images only', 'showcase-creator' ) }
					checked={ attributes.withThumbnail }
					onChange={ ( v ) => { setAttributes( { withThumbnail: v } ) } }
				/>
			</PanelRow>
			<BaseControl
				label={ __( 'Comment count', 'showcase-creator' ) }
				help={ __( 'Leave blank to not count comments', 'showcase-creator' ) }
			>
				<PanelRow className={ 'sc-inspector-controls-row-gap' }>
					<SelectControl
						value={ attributes.comments[1] }
						options={ [
							_o( '> (greater than)', '>' ),
							_o( '< (less than)', '<' ),
							_o( '= (equal to)',	'=' ),
							_o( '!= (not equal to)', '!=' )
						] }
						onChange={ ( v ) => {
							let temp = [ ...attributes.comments ];
							temp[1] = v; setAttributes( { comments: temp } );
						} }
					/>
					<NumberControl
						min={0}
						value={ attributes.comments[0] }
						onChange={ ( v ) => {
							let temp = [ ...attributes.comments ];
							temp[0] = String( v );
							setAttributes( { comments: temp } );
						} }
					/>
				</PanelRow>
			</BaseControl>
			<PanelRow>
				<TextControl
					label={ __( 'Search', 'showcase-creator' ) }
					value={ attributes.search }
					onChange={ ( v ) => setAttributes( { search: v } ) }
				/>
			</PanelRow>
			<PanelRow>
				<SelectControl
					label={ __( 'Search mode', 'showcase-creator' ) }
					value={ attributes.searchMode }
					options={ [
						_o( 'Standard', 's' ),
						_o( 'In title only', 'search_by_title' ),
						_o( 'Title starts with', 'title_starts_with' )
					] }
					onChange={ ( v ) => setAttributes( { searchMode: v } ) }
				/>
			</PanelRow>
			<PanelRow>
				<ToggleControl
					help={ __( 'Caution: For use in templates only.', 'showcase-creator' ) }
					label={ __( 'Use default query?', 'showcase-creator' ) }
					checked={ attributes.wpQuery }
					onChange={ ( v ) => setAttributes( { wpQuery: v } ) }
				/>
			</PanelRow>
		</PanelBody>
	</Panel>
}

/**
 * Custom Server Side Rendering component.
 * 
 * @props attributes - Block attributes.
 * @props initialShouldUpdate - The initial state of the 'Update preview?' toggle.
 */
class RenderShowcase extends React.Component {
	constructor( props ) {
		super( props );
		this.ref = React.createRef();
		this.script = null;
		this.updating = false;
		this.oldAtts = {};
		this.state = { css: '', html: '', shouldUpdate: props.initialShouldUpdate };
	}
	setHTML( html ) {
		const ref = this.ref.current;
		if ( ref ) {
			ref.innerHTML = html;
			[ ...ref.querySelectorAll( 'a', 'button' ) ]
				.forEach( ( l ) => l.href = 'javascript:void(0)');
			if (
				ref.querySelector( '.vid-slider' ) &&
				! ref.querySelector( '.vid-slider-inner' ) &&
				typeof window.showcaseCreator.modules !== 'undefined' &&
				typeof window.showcaseCreator.modules.slider !== 'undefined'
			) {
				this.script = window.showcaseCreator.modules.slider.mount( ref.querySelector( '.vid-slider' ) );
			}
		}
	}
	updatePreview() {
		if ( this.updating || ! this.state.shouldUpdate ) {
			return;
		}
		this.updating = true;
		window.showcaseCreator.rest( {
			get: 'block_preview',
			args: this.props.attributes
		} ).then( r => {
			if ( ! r || typeof r.block_preview === 'undefined' ) {
				return;
			}
			const css = r.block_preview.css;
			const html = r.block_preview.html;
			this.setHTML( html );
			this.setState( { css: css, html: html, shouldUpdate: this.state.shouldUpdate } );
			this.updating = false;
		} );
	}
	onUpdateToggle( v ) {
		this.setState( {
			css: this.state.css,
			html: this.state.html,
			shouldUpdate: v
		} );
		if ( v ) {
			this.updatePreview();
		}
	}
	componentDidMount() {
		this.updatePreview();
	}
	componentDidUpdate() {
		if ( ! window.showcaseCreator.isEqual( this.oldAtts, this.props.attributes ) ) {
			this.oldAtts = { ...this.props.attributes };
			this.updatePreview();
		}
	}
	componentWillUnmount() {
		this.script && ( this.script.unmount() );
	}
	render() {
		return (
			<div className={ "sc-block-preview" }>
				<div className={ "sc-block-preview-options" }>
					<ToggleControl
						label={ __( 'Update preview?', 'showcase-creator' ) }
						checked={ this.state.shouldUpdate }
						onChange={ ( v ) => this.onUpdateToggle( v ) }
					/>
				</div>
				<style className={ "sc-block-preview-css" }>{ this.state.css }</style>
				<div className={ "sc-block-preview-html" } ref={ this.ref }></div>
			</div>
		);
	}
}

const Loading = () => {
	return (
		<div className={ "vid-loading-wrapper" }>
			<div className={ "vid-loading" }>
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<div></div>
			</div>
		</div>
	)
}

const LayoutSelector = ( { attributes, setAttributes } ) => {
	const data = window.showcaseCreator.data;
	return (
		<PanelBody>
			<PanelRow>
				<SelectControl
					label={ __( 'Layout', 'showcase-creator' ) }
					value={ attributes.layout }
					options={ [
						data.layouts.length ?
							_o( 'Select a layout', '', true ) :
							_o( 'No layouts found', '', true ),
						...data.layouts
					] }
					onChange={ ( v ) => setAttributes( { layout: v } ) }
				/>
			</PanelRow>
		</PanelBody>
	);
}

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {WPElement} Element to render.
 */
export default function Edit( { attributes, setAttributes } ) {
	const blockProps = useBlockProps();
	const [ posts, setPosts ] = useState( [] );
	const data = window.showcaseCreator.data;
	const [ status, setStatus ] = useState(
		typeof data === 'object' && 'status' in data ?
			data.status :
			false
	);

	// Initiate data loading
	if ( ! status ) {
		setStatus( 'pending' );
		setTimeout(
			() => loadData( setStatus ).then(
				() => {
					if ( ! attributes.layout ) {
						setAttributes( { layout: data.layouts[0].value } );
					}
					if ( ! posts.length ) {
						loadPosts( attributes.postType, setPosts );
					}
				}
			)
		);
	}

	// Tabs
	const tabs = [
		{
			name: 'query',
			title: __( 'Taxonomy', 'showcase-creator' ),
			content:
				<Tab_Query
					attributes={ attributes }
					setAttributes={ setAttributes }
					data={ data }
					onTypeChange={ ( type ) => loadPosts( type, setPosts ) }
				/>
		},
		{
			name: 'order',
			title: __( 'Order', 'showcase-creator' ),
			content:
				<Tab_Order
					attributes={ attributes }
					setAttributes={ setAttributes }
					data={ data }
				/>
		},
		{
			name: 'authors',
			title: __( 'Authors', 'showcase-creator' ),
			content:
				<Tab_Authors
					attributes={ attributes }
					setAttributes={ setAttributes }
					data={ data }
				/>
		},
		{
			name: 'posts',
			title: __( 'Posts', 'showcase-creator' ),
			content:
				<Tab_Posts
					attributes={ attributes }
					setAttributes={ setAttributes }
					data={ data }
					posts={ posts }
				/>
		},
		{
			name: 'date',
			title: __( 'Date', 'showcase-creator' ),
			content:
				<Tab_Date
					attributes={ attributes }
					setAttributes={ setAttributes }
				/>
		},
		{
			name: 'pagination',
			title: __( 'Paging', 'showcase-creator' ),
			content:
			<Tab_Pagination
			attributes={ attributes }
			setAttributes={ setAttributes }
			/>
		},
		{
			name: 'more',
			title: __( 'More', 'showcase-creator' ),
			content:
				<Tab_More
					attributes={ attributes }
					setAttributes={ setAttributes }
				/>
		},
	];

	return (
		<div { ...blockProps }>
			<InspectorControls>
				{ status !== 'ready' && ( <Loading /> ) }
				{ status === 'ready' && (
					<LayoutSelector attributes={ attributes } setAttributes={ setAttributes } />
				) }
				{ status === 'ready' && (
					<TabPanel
						className={ 'sc-inspector-controls-tab-panel' }
						activeClass='sc-controls-tab-active'
						tabs={ tabs }
					>{
						( { content } ) => content
					}</TabPanel>
				) }
			</InspectorControls>
			<RenderShowcase attributes={ attributes } initialShouldUpdate={ true } />
			{ status !== 'ready' && ( <Loading /> ) }
		</div>
	);
}
